import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import endPoint from 'routes/NetworkRoutes';
import { CLEAR_USER_DATA, MENU_OPEN } from 'store/actions';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Switch,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import UpgradePlanCard from './UpgradePlanCard';
import User1 from 'assets/images/users/user-round.svg';
import RedBubble from 'ui-component/RedBubble';
import { usePendingTasks } from 'context/PendingTasksContext';

// assets
import { IconLogout, IconSearch, IconSettings, IconUser, IconLock, IconEdit } from '@tabler/icons';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const navigate = useNavigate();
    const user = JSON.parse(window.localStorage.getItem('User State'));
    const { counts } = usePendingTasks();
    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const [profilePic, setProfilePic] = useState(null);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const dispatch = useDispatch();
    const anchorRef = useRef(null);
    const handleLogout = async () => {
        console.log('Logout');
        if (user?.user?.Username) {
            sessionStorage.removeItem(`Student_Profile_Pic_${user.user.Username}`);
        }
        dispatch({ type: MENU_OPEN, id: 'default' });
        dispatch({ type: CLEAR_USER_DATA });
        window.localStorage.clear();
        navigate('/login');
    };

    useEffect(() => {
        let isMounted = true;

        const loadProfilePic = async () => {
            const isStudent = parseInt(user?.user?.role) === 4;
            const sid = user?.user?.Username;

            if (!isStudent || !sid) {
                return;
            }

            const cachedPic = sessionStorage.getItem(`Student_Profile_Pic_${sid}`);
            if (cachedPic) {
                if (isMounted) setProfilePic(cachedPic);
                return;
            }

            // 1. Try dedicated endpoint
            try {
                if (endPoint.getStudentProfilePic) {
                    const response = await axios.get(endPoint.getStudentProfilePic(sid), {
                        headers: {
                            Authorization: `Bearer ${user?.token}`
                        }
                    });
                    if (response?.data?.data?.profileUrl) {
                        const url = response.data.data.profileUrl;
                        if (isMounted) setProfilePic(url);
                        sessionStorage.setItem(`Student_Profile_Pic_${sid}`, url);
                        return;
                    }
                }
            } catch (err) {
                console.error('Error fetching via getStudentProfilePic:', err);
            }

            // 2. Fallback to getPermanentStudentData
            try {
                const response = await axios.get(endPoint.getPermanentStudentData(sid), {
                    headers: {
                        Authorization: `Bearer ${user?.token}`
                    }
                });
                const key = response?.data?.data?.findDetails?.ProfileUrl;
                if (key && key !== 'https://' && key.trim() !== '') {
                    if (key.startsWith('http://') || key.startsWith('https://')) {
                        if (isMounted) setProfilePic(key);
                        sessionStorage.setItem(`Student_Profile_Pic_${sid}`, key);
                    } else {
                        const responseUrl = await axios.get(
                            `${endPoint.getPresignedUrlToViewDocuments}?key=${key}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${user?.token}`,
                                    'Access-Control-Allow-Origin': '*'
                                }
                            }
                        );
                        if (responseUrl?.data?.code === 200 && responseUrl?.data?.data?.getUrl) {
                            const url = responseUrl.data.data.getUrl;
                            if (isMounted) setProfilePic(url);
                            sessionStorage.setItem(`Student_Profile_Pic_${sid}`, url);
                        }
                    }
                }
            } catch (fallbackErr) {
                console.error('Error fetching fallback profile pic:', fallbackErr);
            }
        };

        loadProfilePic();

        return () => {
            isMounted = false;
        };
    }, [user?.user?.Username, user?.user?.role, user?.token]);

    const handleManageProfile = () => {
        setOpen(false);
        navigate('/userDashboard/manageProfile');
    };

    const handlePassword = async () => {
        navigate('/changePassword');
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const role = (role) => {
        switch (parseInt(role)) {
            case 1:
                return `Super Admin`;
                break;
            case 2:
                return `Admin`;
                break;
            case 3:
                return `Teacher`;
                break;
            case 4:
                return `Student`;
                break;

            default:
                break;
        }
    };

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src={profilePic || User1}
                        imgProps={{
                            onError: () => setProfilePic(null)
                        }}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    />
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 2 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            {profilePic && (
                                                <Avatar
                                                    src={profilePic}
                                                    imgProps={{
                                                        onError: () => setProfilePic(null)
                                                    }}
                                                    sx={{
                                                        ...theme.typography.largeAvatar,
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            )}
                                            <Stack>
                                                {parseInt(user?.user?.role) === 4 && (
                                                    <Typography variant="h5" color="primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                                                        SID: {user?.user?.Username}
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 600 }}>
                                                        {user?.user?.Name}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="subtitle2">{role(user?.user?.role)}</Typography>
                                            </Stack>
                                        </Stack>
                                        {/* <OutlinedInput
                                            sx={{ width: '100%', pr: 1, pl: 2, my: 2 }}
                                            id="input-search-profile"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                            placeholder="Search profile options"
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
                                                </InputAdornment>
                                            }
                                            aria-describedby="search-helper-text"
                                            inputProps={{
                                                'aria-label': 'weight'
                                            }}
                                        /> */}
                                    </Box>
                                    <Divider />
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ px: 2 }}>
                                            {/* <UpgradePlanCard /> */}
                                            {/* <Divider /> */}
                                            {/* <Card
                                                sx={{
                                                    bgcolor: theme.palette.primary.light,
                                                    my: 2
                                                }}
                                            >
                                                <CardContent>
                                                    <Grid container spacing={3} direction="column">
                                                        <Grid item>
                                                            <Grid item container alignItems="center" justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="subtitle1">Start DND Mode</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Switch
                                                                        color="primary"
                                                                        checked={sdm}
                                                                        onChange={(e) => setSdm(e.target.checked)}
                                                                        name="sdm"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item>
                                                            <Grid item container alignItems="center" justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="subtitle1">Allow Notifications</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Switch
                                                                        checked={notification}
                                                                        onChange={(e) => setNotification(e.target.checked)}
                                                                        name="sdm"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                            <Divider /> */}
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                {/* <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 0}
                                                    onClick={(event) => handleListItemClick(event, 0, '/user/account-profile/profile1')}
                                                >
                                                    <ListItemIcon>
                                                        <IconSettings stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Account Settings</Typography>} />
                                                </ListItemButton> */}
                                                {/* <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 1}
                                                    onClick={(event) => handleListItemClick(event, 1, '/user/social-profile/posts')}
                                                >
                                                    <ListItemIcon>
                                                        <IconUser stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Grid container spacing={1} justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="body2">Social Profile</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Chip
                                                                        label="02"
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: theme.palette.warning.dark,
                                                                            color: theme.palette.background.default
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </ListItemButton> */}
                                                {parseInt(user?.user?.role) === 4 && (
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                        selected={selectedIndex === 3}
                                                        onClick={handleManageProfile}
                                                    >
                                                        <ListItemIcon>
                                                            <IconUser stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">Manage Profile</Typography>} />
                                                        {counts?.profileVerification > 0 && (
                                                            <RedBubble count={counts.profileVerification} size="small" sx={{ ml: 'auto' }} />
                                                        )}
                                                    </ListItemButton>
                                                )}
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handlePassword}
                                                >
                                                    <ListItemIcon>
                                                        <IconLock stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Change Password</Typography>} />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
